// src/components/pedidos/cliente/CPedidosCliente.jsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Table,
  Typography,
  Tag,
  Pagination,
  Modal,
  Card,
  Divider,
  Empty,
  Row,
  Col,
  Button
} from 'antd';
import { LoginContext } from '../../../../context/loginContext';
import { GlobalContext } from '../../../../context/globalContext';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import CEditarPedidoCliente from './CEditarPedidoCliente';
import { getFormaPagoLabel } from '../../../../utils/formaPagoLabel';

const { Title, Text } = Typography;

const CPedidosCliente = () => {
  const { user } = useContext(LoginContext);
  const [pedidos, setPedidos] = useState([]);
  const [total, setTotal] = useState(0);
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [pedidoEditando, setPedidoEditando] = useState(null); // NUEVO
  const [estados, setEstados] = useState({});
  const { criteriosReemplazo, tipoPagos } = useContext(GlobalContext);

  // Cargar estados desde la API
  const fetchEstados = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/estado-pedido`);
      const data = await response.json();
      const estadosArray = data.data; // <- ACCESO CORRECTO
      if (response.ok && Array.isArray(estadosArray)) {
        const mapeo = {};
        estadosArray.forEach((e) => {
          let color = 'default';
          switch (e.epe_id) {
            case 1: color = 'gold'; break;
            case 2: color = 'blue'; break;
            case 3: color = 'green'; break;
            case 4: color = 'red'; break;
          }
          mapeo[e.epe_id] = {
            label: e.epe_nombre,
            color
          };
        });
        setEstados(mapeo);
      }
    } catch (error) {
      console.error('Error al obtener estados:', error.message);
    }
  };

  const fetchPedidos = async (pagina = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/pedido/cliente?page=${pagina}&limit=10`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setPedidos(data.pedidos);
        setTotal(data.total);
        setPaginaActual(pagina);
      } else {
        console.error('Error al obtener pedidos:', data.message);
      }
    } catch (err) {
      console.error('Error al obtener pedidos:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEstados();
    fetchPedidos();
  }, []);

  const columnas = [
    {
      title: 'Fecha',
      dataIndex: 'ped_fecha',
      key: 'fecha',
      render: (fecha) => new Date(fecha).toLocaleString()
    },
    {
      title: 'Estado',
      dataIndex: 'ped_estado',
      key: 'estado',
      render: (estado) => {
        const est = estados[estado] || {};
        return (
          <Tag color={est.color || 'default'}>
            {est.label || 'Desconocido'}
          </Tag>
        );
      }
    },
    {
      title: 'Total',
      dataIndex: 'ped_total',
      key: 'total',
      render: (total) => `$${Number(total).toFixed(2)}`
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 12 }}>
          <EyeOutlined
            onClick={() => setPedidoSeleccionado(record)}
            style={{ fontSize: 18, cursor: 'pointer', color: '#1890ff' }}
            title="Ver detalle"
          />
          {Number(record.ped_estado) === 1 && (
            <EditOutlined
              onClick={() => setPedidoEditando(record)}
              style={{ fontSize: 18, cursor: 'pointer', color: '#52c41a' }}
              title="Editar pedido"
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      {pedidos.length === 0 && !loading ? (
        <Empty description="No tenés pedidos aún" />
      ) : (
        <>
          <Table
            columns={columnas}
            dataSource={pedidos}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowKey="ped_id"
          />

          <Pagination
            current={paginaActual}
            total={total}
            pageSize={10}
            onChange={fetchPedidos}
            style={{ marginTop: 16, textAlign: 'right' }}
          />
        </>
      )}

      {/* MODAL DE DETALLE */}
      <Modal
        title="Detalle del pedido"
        open={!!pedidoSeleccionado}
        onCancel={() => setPedidoSeleccionado(null)}
        footer={null}
        width="100%"
        style={{ top: 20 }}
      >
        {pedidoSeleccionado && (
          <Row gutter={[0, 16]} direction="vertical">
            <Col span={24}>
              <Card title="Artículos del pedido">
                {pedidoSeleccionado.detalles?.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 12
                    }}
                  >
                    <div>
                      <Text strong>{item.aem_nombre}</Text> <br />
                      <Text type="secondary">x{item.dpe_cantidad}</Text>
                    </div>
                    <Text>${Number(item.dpe_total).toFixed(2)}</Text>
                  </div>
                ))}
                <Divider />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Total:</Text>
                  <Text strong>${Number(pedidoSeleccionado.ped_total).toFixed(2)}</Text>
                </div>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Datos del pedido" style={{ marginBottom: 16 }}>
                <p><Text strong>Fecha:</Text> {new Date(pedidoSeleccionado.ped_fecha).toLocaleString()}</p>
                <p>
                  <Text strong>Estado:</Text>{' '}
                  <Tag color={estados[pedidoSeleccionado.ped_estado]?.color}>
                    {estados[pedidoSeleccionado.ped_estado]?.label}
                  </Tag>
                </p>
                <p><Text strong>Forma de pago:</Text> {getFormaPagoLabel(pedidoSeleccionado, tipoPagos)}</p>
                <p><Text strong>Tipo de envío:</Text> {pedidoSeleccionado.ped_tipoenvio === 2 ? 'Retiro en local' : 'Envío a domicilio'}</p>
                {pedidoSeleccionado.ped_criterioreemplazo && (
                  <p>
                    <Text strong>Criterio de reemplazo:</Text>{' '}
                    {
                      criteriosReemplazo.find(c => c.cre_id === pedidoSeleccionado.ped_criterioreemplazo)
                        ?.cre_descripcion || 'N/A'
                    }
                  </p>
                )}
                {pedidoSeleccionado.ped_productos_solicitados && (
                  <p>
                    <Text strong>Productos solicitados:</Text>{' '}
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {pedidoSeleccionado.ped_productos_solicitados}
                    </span>
                  </p>
                )}
                {pedidoSeleccionado.ped_observaciones && (
                  <p>
                    <Text strong>Observaciones:</Text>{' '}
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {pedidoSeleccionado.ped_observaciones}
                    </span>
                  </p>
                )}
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Sucursal" style={{ marginBottom: 16 }}>
                <Text>{pedidoSeleccionado.suc_nombre}</Text>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Dirección">
                {pedidoSeleccionado.dci_direccion ? (
                  <>
                    <Text>{pedidoSeleccionado.dci_direccion}</Text> <br />
                    <Text type="secondary">
                      {pedidoSeleccionado.dci_localidad} – CP {pedidoSeleccionado.dci_codigoposta}
                    </Text>
                    <br />
                    {pedidoSeleccionado.dci_observacion && (
                      <Text type="secondary">
                        Observación: {pedidoSeleccionado.dci_observacion}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text>Retiro en local</Text>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Modal>

      {/* EDITOR PARA CLIENTE (solo estado 1) */}
      {pedidoEditando && (
        <CEditarPedidoCliente
          visible={!!pedidoEditando}
          pedido={pedidoEditando}
          onClose={() => setPedidoEditando(null)}
          onGuardarCambios={() => fetchPedidos(paginaActual)}
        />
      )}
    </div>
  );
};

export default CPedidosCliente;